/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// UI lib components
import { notifications } from '@mantine/notifications';

// Lib
import { apiClient } from '@lib/api-client';
import { TMutationConfig } from '@lib/react-query';

// Types
import {
	TApiResponseProducerKpis,
	TBodyProducerKpis,
} from '../types';

// Types
import { TApiError } from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const statisticsProducerKpis = (
	body: TBodyProducerKpis
): Promise<TApiResponseProducerKpis> => {
	return apiClient.post('/statistics/kpis/producers', {
		...body,
		withCredentials: true,
	});
};

type TUseProducerKpisOptions = {
	mutationConfig?: TMutationConfig<
		typeof statisticsProducerKpis
	>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useProducerKpis = ({
	mutationConfig,
}: TUseProducerKpisOptions) => {
	const { t } = useTranslation();
	const { onSuccess } = mutationConfig || {};

	return useMutation({
		mutationFn: statisticsProducerKpis,
		mutationKey: [
			queryKeys.app.dashboard.kpisProducers.key,
		],
		onSuccess: (...args) => {
			onSuccess?.(...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});
};
